import {
    faCheckCircle,
    faClose,
    faExclamationTriangle,
    faInfoCircle,
    faWarning
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactNode, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { Subject } from "rxjs";
import { CommonHelper } from "src/utils/common-helper";

export type AlertDialogType = "info" | "success" | "danger" | "warning";
export type AlertDialogSize = "lg" | "md";

export interface AlertDialogConfig {
    content: ReactNode;
    title?: string;
    type: AlertDialogType;
    size?: AlertDialogSize;
    hasButton?: boolean;
    buttonText?: string;
    onClose?: () => void;
    isForException?: boolean;
    className?: string;
    header?: ReactNode;
}

interface InternalAlertDialog extends AlertDialogConfig {
    dialogInternalId: string;
}

const AlertDialogContainerComponent: React.FC = () => {
    const [dialogBoxes, setDialogBoxes] = useState<InternalAlertDialog[]>([]);
    const [closingIds, setClosingIds] = useState<Set<string>>(new Set());
    const [enteringIds, setEnteringIds] = useState<Set<string>>(new Set());

    const handleClose = (dialogInternalId: string) => {
        if (closingIds.has(dialogInternalId)) return;

        setClosingIds(prev => new Set(prev).add(dialogInternalId));
        setTimeout(() => {
            setDialogBoxes(prev => {
                const toClose = prev.find(x => x.dialogInternalId === dialogInternalId);
                if (toClose?.onClose) toClose.onClose();
                return prev.filter(x => x.dialogInternalId !== dialogInternalId);
            });

            setClosingIds(prev => {
                const next = new Set(prev);
                next.delete(dialogInternalId);
                return next;
            });

            setEnteringIds(prev => {
                const next = new Set(prev);
                next.delete(dialogInternalId);
                return next;
            });
        }, 300);
    };

    const handleOpen = (config: AlertDialogConfig) => {
        const dialogInternalId = CommonHelper.generateRandomString(20);
        setDialogBoxes(prev => [...prev, { ...config, dialogInternalId }]);

        requestAnimationFrame(() => {
            setEnteringIds(prev => new Set(prev).add(dialogInternalId));
        });
    };

    // ESC close for topmost dialog
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && dialogBoxes.length > 0) {
                const topDialogId = dialogBoxes[dialogBoxes.length - 1].dialogInternalId;
                handleClose(topDialogId);
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [dialogBoxes]);

    useEffect(() => {
        const sub = AlertDialog.AlertDialogGenerateRequestSubject.subscribe(config => {
            handleOpen(config);
        });
        return () => sub.unsubscribe();
    }, []);

    return (
        <div className="alert-dialogs-container">
            {dialogBoxes.map((config, index) => {
                const isClosing = closingIds.has(config.dialogInternalId);
                const isEntering = enteringIds.has(config.dialogInternalId);
                const zIndexBase = 9000 + index * 2;

                return (
                    <div className="alert-dialog" key={`alert-dialog-${index}`}>
                        {/* Backdrop */}
                        <div
                            dialog-backdrop-id={config.dialogInternalId}
                            className={`backdrop fade ${isEntering && !isClosing ? "show" : ""}`}
                            onClick={() => handleClose(config.dialogInternalId)}
                            style={{ zIndex: zIndexBase }}
                        />

                        {/* Modal Container */}
                        <div
                            dialog-id={config.dialogInternalId}
                            className={[
                                "dialog",
                                config.type,
                                config.size ?? "md",
                                config.isForException ? "for-exception" : "",
                                config.className ?? "",
                                isClosing ? "" : isEntering ? "show" : ""
                            ].join(" ")}
                            style={{ zIndex: zIndexBase + 1 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="custom-close" onClick={() => handleClose(config.dialogInternalId)}>
                                <FontAwesomeIcon icon={faClose} />
                            </div>

                            <div className="content-wrapper">
                                <div className="header">
                                    {config.header ? (
                                        config.header
                                    ) : (
                                        <>
                                            <div className="icon animate__animated animate__tada">
                                                {config.type === "danger" && <FontAwesomeIcon icon={faExclamationTriangle} color="var(--bs-danger)" />}
                                                {config.type === "info" && <FontAwesomeIcon icon={faInfoCircle} color="var(--bs-info)" />}
                                                {config.type === "warning" && <FontAwesomeIcon icon={faWarning} color="var(--bs-warning)" />}
                                                {config.type === "success" && <FontAwesomeIcon icon={faCheckCircle} color="var(--bs-success)" />}
                                            </div>
                                            <div className={`modal-title text-${config.type}`}>{config.title}</div>
                                        </>
                                    )}
                                </div>

                                {typeof config.content === "string" ? (
                                    <div
                                        className={`main-content ${config.isForException ? "for-exception" : ""}`}
                                        dangerouslySetInnerHTML={{ __html: CommonHelper.nl2br(config.content) }}
                                    />
                                ) : (
                                    <div className={`main-content ${config.isForException ? "for-exception" : ""}`}>
                                        {config.content}
                                    </div>
                                )}
                            </div>

                            {config.hasButton && (
                                <div className="footer">
                                    <Button variant={config.type} onClick={() => handleClose(config.dialogInternalId)}>
                                        {config.buttonText}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default AlertDialogContainerComponent;

export class AlertDialog {
    static AlertDialogGenerateRequestSubject = new Subject<AlertDialogConfig>();

    static show(config: AlertDialogConfig) {
        this.AlertDialogGenerateRequestSubject.next(config);
    }
}
